// src/ui/screens/Result.tsx
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db, type ResultSummary, type Session } from "../../storage/db";
import { colors } from "../design-system/colors";
import { fontFamily, typeScale } from "../design-system/typography";
import { space, radius, minTapTarget } from "../design-system/spacing";
import ScoreBadge from "../components/ScoreBadge";
import RiskGauge from "../components/RiskGauge";
import { track } from "../../services/analytics";

export default function Result() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [summary, setSummary] = useState<ResultSummary | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    void (async () => {
      const s = await db.sessions.get(sessionId);
      const r = await db.resultSummaries.get(sessionId);
      setSession(s ?? null);
      setSummary(r ?? null);
    })();
  }, [sessionId]);

  if (!session || !summary) {
    return <div style={styles.loading}>読み込み中…</div>;
  }

  const handleExportPdf = () => {
    track("pdf_exported", { sessionId: sessionId ?? "" });
    // TODO: docx/pdfスキル相当のクライアントサイドPDF生成と接続(14章のエクスポート機能)
    window.print();
  };

  return (
    <div style={styles.container}>
      <button onClick={() => navigate("/measure")} style={styles.backButton}>
        ← 戻る
      </button>

      <h1 style={styles.title}>
        {session.exerciseType === "DEADLIFT" ? "デッドリフト" : "スクワット"}の結果
      </h1>

      <div style={styles.scoreRow}>
        <ScoreBadge score={summary.overallScore} />
      </div>

      <RiskGauge
        riskLevel={summary.riskLevel}
        compressionN={summary.maxCompressionForce}
        shearN={summary.maxShearForce}
      />

      <div style={styles.adviceCard}>
        <div style={styles.adviceLabel}>コーチからのひとこと</div>
        <p style={styles.adviceText}>{summary.slmAdviceText}</p>
      </div>

      <div style={styles.actionRow}>
        <button style={styles.secondaryButton} onClick={() => navigate("/history")}>
          履歴を見る
        </button>
        <button style={styles.secondaryButton} onClick={() => navigate("/compare")}>
          比較する
        </button>
        <button style={styles.primaryButton} onClick={handleExportPdf}>
          PDFで保存
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100dvh",
    background: colors.bg,
    color: colors.textPrimary,
    fontFamily: fontFamily.ui,
    padding: space.lg,
    maxWidth: 480,
    margin: "0 auto",
  },
  loading: {
    minHeight: "100dvh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: colors.bg,
    color: colors.textSecondary,
    fontFamily: fontFamily.ui,
  },
  backButton: {
    background: "none",
    border: "none",
    color: colors.textSecondary,
    fontFamily: fontFamily.ui,
    fontSize: typeScale.body.size,
    padding: 0,
    marginBottom: space.md,
    minHeight: minTapTarget,
    cursor: "pointer",
  },
  title: { fontSize: typeScale.h1.size, marginBottom: space.lg },
  scoreRow: { display: "flex", justifyContent: "center", marginBottom: space.lg },
  adviceCard: {
    marginTop: space.lg,
    padding: space.md,
    borderRadius: radius.md,
    background: colors.surface,
    border: `1px solid ${colors.surfaceBorder}`,
  },
  adviceLabel: {
    fontSize: typeScale.caption.size,
    color: colors.amber,
    fontWeight: 600,
    marginBottom: space.xs,
  },
  adviceText: {
    fontSize: typeScale.body.size,
    lineHeight: typeScale.body.lineHeight,
    color: colors.textPrimary,
    margin: 0,
  },
  actionRow: {
    display: "flex",
    gap: space.sm,
    marginTop: space.xl,
  },
  secondaryButton: {
    flex: 1,
    minHeight: minTapTarget,
    borderRadius: radius.md,
    border: `1.5px solid ${colors.surfaceBorder}`,
    background: "transparent",
    color: colors.textSecondary,
    fontFamily: fontFamily.ui,
    fontSize: typeScale.caption.size,
  },
  primaryButton: {
    flex: 1,
    minHeight: minTapTarget,
    borderRadius: radius.md,
    border: "none",
    background: colors.amber,
    color: colors.bg,
    fontFamily: fontFamily.ui,
    fontWeight: 700,
    fontSize: typeScale.caption.size,
  },
};
